import {
  AddCustomerParentMutation,
  AddCustomerParentMutationVariables,
  DeleteCustomerParentMutation,
  DeleteCustomerParentMutationVariables,
  SetCustomerParentValidToMutation,
  SetCustomerParentValidToMutationVariables,
} from "@/gql/graphql";
import { gql, useMutation } from "@apollo/client";
import { useCallback } from "react";

const addParentMutation = gql`
  mutation AddCustomerParent($input: AddCustomerParentInput!) {
    productPrice {
      customer {
        addParent(input: $input)
      }
    }
  }
`;

const setParentValidToMutation = gql`
  mutation SetCustomerParentValidTo($input: SetCustomerParentValidToInput!) {
    productPrice {
      customer {
        setParentValidTo(input: $input)
      }
    }
  }
`;

const deleteParentMutation = gql`
  mutation DeleteCustomerParent($input: CustomerParentKeyInput!) {
    productPrice {
      customer {
        deleteParent(input: $input)
      }
    }
  }
`;

/**
 * Named so the three writes can ask for the read to run again.
 *
 * Awaited, so a write is not finished until the read that follows it has
 * landed. What that buys is the editor closing onto rows that already show
 * the change, rather than onto the old ones for the moment the refetch
 * takes. The wait is held on the button that started it.
 */
const refetch = {
  refetchQueries: ["CustomerPriceParent"],
  awaitRefetchQueries: true,
};

export interface ParentStretchKey {
  parentCustomerNo: number;
  /** yyyy-MM-dd. */
  validFrom: string;
}

/**
 * The three writes, and one loading and error between them.
 *
 * They all return a boolean and nothing else, so the table is refreshed by
 * running the read again rather than by patching what is on screen. That
 * also means the rules the backend applies - which are the ones that
 * count - are what ends up displayed.
 *
 * The three share an error because the page shows one banner: whichever
 * write was last attempted is the one that failed.
 */
export const useParentMutations = (customerNo: number) => {
  const [add, addState] = useMutation<
    AddCustomerParentMutation,
    AddCustomerParentMutationVariables
  >(addParentMutation, refetch);

  const [setValidTo, setValidToState] = useMutation<
    SetCustomerParentValidToMutation,
    SetCustomerParentValidToMutationVariables
  >(setParentValidToMutation, refetch);

  const [remove, removeState] = useMutation<
    DeleteCustomerParentMutation,
    DeleteCustomerParentMutationVariables
  >(deleteParentMutation, refetch);

  return {
    loading: addState.loading || setValidToState.loading || removeState.loading,
    error: addState.error ?? setValidToState.error ?? removeState.error,

    addParent: useCallback(
      (parentCustomerNo: number, validFrom: string) =>
        add({
          variables: { input: { customerNo, parentCustomerNo, validFrom } },
        }),
      [add, customerNo]
    ),

    setParentValidTo: useCallback(
      (key: ParentStretchKey, validTo: string) =>
        setValidTo({
          variables: { input: { key: { customerNo, ...key }, validTo } },
        }),
      [setValidTo, customerNo]
    ),

    deleteParent: useCallback(
      (key: ParentStretchKey) =>
        remove({ variables: { input: { customerNo, ...key } } }),
      [remove, customerNo]
    ),
  };
};
